// src/hooks/useExpenses.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { expenseApi } from '../api/expenseApi';
import toast from 'react-hot-toast';

const DEFAULT_FILTERS = {
  search: '', category: '', projectId: '', validated: '',
  startDate: '', endDate: '', minAmount: '', maxAmount: '',
  supplier: '', paymentMethod: '', sortBy: 'createdAt',
  page: 1, limit: 15,
};

export const useExpenses = (externalFilters = {}) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0, limit: 15 });

  const isMounted = useRef(true);
  const filtersRef = useRef({ ...DEFAULT_FILTERS, ...externalFilters });

  useEffect(() => {
    filtersRef.current = { ...DEFAULT_FILTERS, ...externalFilters };
  });

  const fetchExpenses = useCallback(async () => {
    if (!isMounted.current) return;
    setLoading(true);
    setError(null);

    try {
      // Backend: { success, data: [...], total, page, totalPages }
      const res = await expenseApi.getAll(filtersRef.current);
      if (!isMounted.current) return;

      const list = Array.isArray(res.data) ? res.data : [];
      const total = res.total ?? res.pagination?.total ?? list.length;
      const pages = res.totalPages ?? res.pagination?.totalPages ?? 1;
      const page = res.page ?? res.pagination?.page ?? 1;

      setExpenses(list.filter(Boolean));
      setPagination({ page, total, totalPages: pages, limit: filtersRef.current.limit });
    } catch (err) {
      if (!isMounted.current) return;
      setError(err?.response?.data?.message || 'Errore caricamento spese');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
  }, [JSON.stringify(externalFilters)]); // eslint-disable-line

  useEffect(() => { return () => { isMounted.current = false; }; }, []);

  const createExpense = async (data) => {
    try {
      const res = await expenseApi.create(data);
      toast.success('Spesa creata con successo!');
      fetchExpenses();
      return { success: true, data: res.data };
    } catch (err) {
      const message = err?.response?.data?.message || 'Errore creazione';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateExpense = async (id, data) => {
    try {
      const res = await expenseApi.update(id, data);
      toast.success('Spesa aggiornata!');
      fetchExpenses();
      return { success: true, data: res.data };
    } catch (err) {
      const message = err?.response?.data?.message || 'Errore aggiornamento';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const deleteExpense = async (id) => {
    try {
      await expenseApi.delete(id);
      toast.success('Spesa eliminata!');
      fetchExpenses();
      return { success: true };
    } catch (err) {
      const message = err?.response?.data?.message || 'Errore eliminazione';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const validateExpense = async (id) => {
    try {
      const res = await expenseApi.validate(id);
      toast.success('Spesa validata!');
      fetchExpenses();
      return { success: true, data: res.data };
    } catch (err) {
      const message = err?.response?.data?.message || 'Errore validazione';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  return {
    expenses,
    loading,
    error,
    pagination,
    createExpense,
    updateExpense,
    deleteExpense,
    validateExpense,
    refreshExpenses: fetchExpenses,
  };
};